import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { FilterState } from './filters';
import { SampleMark } from './sampleMark';

/**
 * The line under the results bar: how the list is sorted, and, when the city
 * file's dates were shifted forward, a notice saying so. On every layout,
 * including a phone, whose results bar has moved into its header.
 */

const Row = styled('div')({
  // Over the list's top fade (5), as the date headers are.
  position: 'relative',
  zIndex: 6,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '6px 16px',
  margin: '8px 0 4px',
  fontFamily: FONTS.body,
  fontSize: 12,
  color: 'rgba(243,238,231,0.7)',
});

const Sort = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  marginLeft: 'auto',
});

const SortButton = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: '3px 8px',
  borderRadius: 999,
  font: 'inherit',
  fontWeight: 700,
  color: 'rgba(243,238,231,0.6)',
  cursor: 'pointer',
  '&[aria-pressed="true"]': { background: 'rgba(243,238,231,0.14)', color: BRAND.parchment },
  '&:disabled': { opacity: 0.4, cursor: 'default' },
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 1 },
});


interface Props {
  sort: FilterState['sort'];
  /** Nearest first needs a home location (Session 5). */
  canNearest: boolean;
  onSort: (sort: FilterState['sort']) => void;
  /** The file's `datesShifted`. */
  shifted: boolean;
  /** The list sorts; the calendar and the map have their own order, and draw the notice alone. */
  sortable?: boolean;
}

export default function ResultsNotes({ sort, canNearest, onSort, shifted, sortable = true }: Props) {
  if (!sortable && !shifted) return null;
  return (
    <Row>
      {shifted && (
        <span>
          Sample dates — shifted forward so the demo stays current.
          <SampleMark>Sample</SampleMark>
        </span>
      )}
      {sortable && (
        <Sort role="group" aria-label="Sort">
          <SortButton type="button" aria-pressed={sort === 'soonest'} onClick={() => onSort('soonest')}>
            Soonest first
          </SortButton>
          <Tooltip title={canNearest ? '' : 'Set your home location first'} placement="top" arrow>
            {/* A span, so the tooltip still answers over a disabled button. */}
            <span>
              <SortButton
                type="button"
                aria-pressed={sort === 'nearest'}
                disabled={!canNearest}
                onClick={() => onSort('nearest')}
              >
                Nearest first
              </SortButton>
            </span>
          </Tooltip>
        </Sort>
      )}
    </Row>
  );
}
